import dayjs from "dayjs";
// Checks if a provided date is today
const isToday = (date) => {
    const today = new Date();
    return (date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear());
};
// Formats an ISO 8601 string into a format like so: Oct 16, 2023
const formatDate = (inputDateString) => {
    if (!inputDateString)
        return "None";
    const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC"
    };
    const inputDate = new Date(inputDateString);
    return inputDate.toLocaleDateString("en-US", options);
};
// Formats an ISO 8601 string into a format like so: Oct 16, 2023 - 10:51 am
const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
    ];
    // Format hours and minutes
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    const formattedTime = `${hours % 12 || 12}:${minutes} ${ampm}`;
    // Format date
    if (isToday(date)) {
        return `Today - ${formattedTime}`;
    }
    else {
        return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} - ${formattedTime}`;
    }
};
// Calculates the age of a patient given their date of birth. Returns an object with the years and months.
const calculateAge = (birthDateString) => {
    if (!birthDateString)
        return { years: 0, months: 0 };
    const birthDate = new Date(birthDateString);
    const today = new Date();
    let ageYears = today.getFullYear() - birthDate.getFullYear();
    let ageMonths = today.getMonth() - birthDate.getMonth();
    if (today.getDate() < birthDate.getDate()) {
        ageMonths--;
    }
    // Adjust years and months
    if (ageMonths < 0) {
        ageYears--;
        ageMonths += 12;
    }
    return { years: ageYears, months: ageMonths };
};
// Returns a string representing the relative time from a given date
// For example: 2.3 years, 3 weeks, or 4 months, etc...
export function getRelativeTimeFromDate(date, useAgo = false) {
    if (!date)
        return "N/A";
    // Convert string to Date if necessary
    const providedDate = !(date instanceof Date) ? new Date(date) : date;
    const now = new Date();
    let diffInSeconds = Math.abs((now.getTime() - providedDate.getTime()) / 1000); // Difference in seconds
    const timeUnits = [
        { label: "year", seconds: 365 * 24 * 60 * 60 }, // 1 year
        { label: "month", seconds: 30 * 24 * 60 * 60 }, // Approx 1 month
        { label: "week", seconds: 7 * 24 * 60 * 60 }, // 1 week
        { label: "day", seconds: 24 * 60 * 60 }, // 1 day
        { label: "hour", seconds: 60 * 60 }, // 1 hour
        { label: "minute", seconds: 60 }, // 1 minute
        { label: "second", seconds: 1 } // 1 second
    ];
    for (const unit of timeUnits) {
        const unitValue = Math.floor(diffInSeconds / unit.seconds);
        if (unitValue > 0) {
            const remainder = diffInSeconds % unit.seconds;
            const decimalValue = remainder / unit.seconds;
            const totalValue = unitValue + decimalValue;
            // Return only the first significant unit (with 1 decimal if necessary)
            if (decimalValue > 0) {
                // Check if the decimal value of totalValue.toFixed(1) is a whole number
                const totalValue = unitValue + decimalValue;
                const relativeTime = `${totalValue.toFixed(1)} ${unit.label}${totalValue !== 1 ? "s" : ""}`;
                return useAgo ? `${relativeTime} ago` : relativeTime;
            }
            else {
                const relativeTime = `${unitValue} ${unit.label}${unitValue > 1 ? "s" : ""}`;
                return useAgo ? `${relativeTime} ago` : relativeTime;
            }
        }
    }
    return "just now";
}
const toDate = (d) => {
    const date = new Date(d);
    if (isNaN(date.getTime()))
        throw new Error("Invalid date: " + d);
    return date;
};
const isDateBetweenDates = (dateToCheck, startDate, endDate) => {
    try {
        const check = toDate(dateToCheck);
        const start = toDate(startDate);
        const end = toDate(endDate);
        return check >= start && check <= end;
    }
    catch (error) {
        console.error(error);
        return false;
    }
};
const formatToDateOnly = (value) => dayjs(value).format("YYYY-MM-DD");
const buildTrailingDateRange = (days) => [
    dayjs()
        .subtract(days - 1, "day")
        .startOf("day")
        .toDate(),
    dayjs().endOf("day").toDate()
];
const buildYearToDateRange = () => [
    dayjs().startOf("year").toDate(),
    dayjs().endOf("day").toDate()
];
const isSameDate = (left, right) => {
    if (!left && !right)
        return true;
    if (!left || !right)
        return false;
    return (dayjs(left).format("YYYY-MM-DD") === dayjs(right).format("YYYY-MM-DD"));
};
const isSameDateRange = (left, right) => isSameDate(left[0], right[0]) && isSameDate(left[1], right[1]);
const formatDateRangeLabel = (startDate, endDate, fallback = "Select date range") => {
    if (!startDate || !endDate) {
        return fallback;
    }
    return `${dayjs(startDate).format("MMM D, YYYY")} - ${dayjs(endDate).format("MMM D, YYYY")}`;
};
export { buildTrailingDateRange, buildYearToDateRange, calculateAge, formatDate, formatDateRangeLabel, formatDateTime, formatToDateOnly, isDateBetweenDates, isSameDate, isSameDateRange, isToday };
